/**
 * WorkSessionContext.js
 * ======================
 * Tracks:
 *   - Current user's role (admin | chemist | viewer)
 *   - Active work session (start/end, duration timer)
 *   - Session-level edit count
 *
 * Wraps the app so any component can call useWorkSession().
 */

import React, {
  createContext, useContext, useState, useEffect,
  useRef, useCallback
} from 'react';
import { useAuth } from './AuthContext';
import {
  getUserProfile, upsertUserProfile,
  startWorkSession, endWorkSession, incrementSessionEdits
} from '../firebase/firestoreService';

const WorkSessionContext = createContext({});
export const useWorkSession = () => useContext(WorkSessionContext);

export function WorkSessionProvider({ children }) {
  const { currentUser } = useAuth();

  const [userProfile, setUserProfile]   = useState(null);  // { role, displayName }
  const [sessionId, setSessionId]       = useState(null);
  const [sessionStart, setSessionStart] = useState(null);  // Date
  const [editCount, setEditCount]       = useState(0);
  const [compoundsTouched, setCompoundsTouched] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const sessionIdRef        = useRef(null);
  const sessionStartRef     = useRef(null);
  const editCountRef        = useRef(0);
  const compoundsTouchedRef = useRef([]);

  // Keep refs in sync with state (for use in event listeners / cleanup)
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { sessionStartRef.current = sessionStart; }, [sessionStart]);
  useEffect(() => { editCountRef.current = editCount; }, [editCount]);
  useEffect(() => { compoundsTouchedRef.current = compoundsTouched; }, [compoundsTouched]);

  // ── Load user profile & start session on login ─────────────────────────
  useEffect(() => {
    if (!currentUser) {
      setUserProfile(null);
      setLoadingProfile(false);
      return;
    }

    let mounted = true;
    setLoadingProfile(true);

    (async () => {
      // Load or create user profile
      let profile = await getUserProfile(currentUser.uid);
      if (!profile) {
        // First-time login — create a viewer profile
        profile = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || currentUser.email.split('@')[0],
          role: 'viewer',
          createdAt: new Date().toISOString(),
        };
        await upsertUserProfile(currentUser.uid, profile);
      }
      if (!mounted) return;
      setUserProfile(profile);
      setLoadingProfile(false);

      // Start a work session for chemist and admin roles
      if (profile.role === 'chemist' || profile.role === 'admin') {
        const sid = await startWorkSession({
          uid: currentUser.uid,
          displayName: profile.displayName || currentUser.email,
        });
        if (!mounted) return;
        setSessionId(sid);
        setSessionStart(new Date());
        setEditCount(0);
        setCompoundsTouched([]);
      }
    })();

    return () => { mounted = false; };
  }, [currentUser]);

  // ── End session on logout / tab close ─────────────────────────────────
  useEffect(() => {
    const handleUnload = () => {
      const sid = sessionIdRef.current;
      const start = sessionStartRef.current;
      if (!sid || !start) return;
      const durationSec = Math.round((Date.now() - start.getTime()) / 1000);
      // Use sendBeacon for reliability on tab close
      try {
        endWorkSession(sid, {
          durationSec,
          editCount: editCountRef.current,
          compoundsTouched: compoundsTouchedRef.current,
        });
      } catch (_) {}
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // End session when user logs out (currentUser goes null)
  const prevUserRef = useRef(null);
  useEffect(() => {
    const prevUser = prevUserRef.current;
    prevUserRef.current = currentUser;
    if (prevUser && !currentUser) {
      const sid = sessionIdRef.current;
      const start = sessionStartRef.current;
      if (sid && start) {
        const durationSec = Math.round((Date.now() - start.getTime()) / 1000);
        endWorkSession(sid, {
          durationSec,
          editCount: editCountRef.current,
          compoundsTouched: compoundsTouchedRef.current,
        });
        setSessionId(null);
        setSessionStart(null);
        setEditCount(0);
        setCompoundsTouched([]);
      }
    }
  }, [currentUser]);

  // ── Record an edit ─────────────────────────────────────────────────────
  const recordEdit = useCallback((compoundId) => {
    setEditCount(n => n + 1);
    setCompoundsTouched(prev =>
      prev.includes(compoundId) ? prev : [...prev, compoundId]
    );
    incrementSessionEdits(sessionIdRef.current, compoundId);
  }, []);

  // ── Computed values ────────────────────────────────────────────────────
  const isAdmin   = userProfile?.role === 'admin';
  const isChemist = userProfile?.role === 'chemist';
  const canEdit   = isAdmin || isChemist;

  const sessionDuration = sessionStart
    ? Math.round((Date.now() - sessionStart.getTime()) / 1000)
    : 0;

  return (
    <WorkSessionContext.Provider value={{
      userProfile,
      loadingProfile,
      isAdmin,
      isChemist,
      canEdit,
      sessionId,
      sessionStart,
      sessionDuration,
      editCount,
      compoundsTouched,
      recordEdit,
    }}>
      {children}
    </WorkSessionContext.Provider>
  );
}
